import { Fixture } from '../types';
import {
  defaultAxisForStripContext,
  maskScaleForStripContext,
  normalizeStripContext,
  normalizeStripAxis,
} from './stripLightContext';
import { tryDrawSpatialBeamMask } from './spatialBeamMask';
import type { StripAxis, StripContext } from './stripLightContext';

/** 手动 cove：indirectCore 渐变峰值 Alpha 上限，减轻「白线灯管」 */
const MANUAL_COVE_CORE_ALPHA_MAX = 0.8;

/**
 * 手动 cove_light：将蒙版中心从「槽缝锚点」向受光面平移 5–10px，避免中心落在死角诱发画实体灯条。
 * 优先用法线在图像平面上的投影；否则按 stripContext / stripAxis 启发式。
 */
function manualCoveIlluminatedSurfaceOffsetPx(
  fixture: Fixture,
  stripCtx: StripContext,
  axis: StripAxis,
  cx: number,
  cy: number,
  imgW: number,
  imgH: number,
  stripRotationDeg: number,
): { dx: number; dy: number } {
  const mag = 8;
  const n = fixture.spatialNormal;
  if (n && typeof n.nx === 'number' && typeof n.ny === 'number') {
    const hx = n.nx;
    const hy = -n.ny;
    const hlen = Math.hypot(hx, hy);
    if (hlen > 0.04) {
      return { dx: (hx / hlen) * mag, dy: (hy / hlen) * mag };
    }
  }

  const topLike: StripContext[] = [
    'ceiling_cove',
    'floating_ceiling',
    'curtain_pocket',
    'beam_soften',
    'ac_edge',
    'building_outline',
    'tree_ring',
  ];
  const upWash: StripContext[] = ['skirting', 'bed_skirt', 'stair_nosing', 'floating_cabinet'];
  const downWash: StripContext[] = [
    'under_cabinet',
    'glass_display',
    'shelf_front',
    'drawer',
    'wardrobe',
    'bath_tub',
    'shower_niche',
    'toilet_guide',
    'out_rail',
    'out_seat',
  ];
  const wallLike: StripContext[] = [
    'wall_feature',
    'wall_panel',
    'niche',
    'door_frame',
    'corner_guide',
    'mirror_back',
    'stair_handrail',
  ];

  if (topLike.includes(stripCtx)) return { dx: 0, dy: mag };
  if (upWash.includes(stripCtx)) return { dx: 0, dy: -mag };
  if (downWash.includes(stripCtx)) return { dx: 0, dy: mag };
  if (wallLike.includes(stripCtx)) {
    const toward = cx < imgW * 0.5 ? mag : -mag;
    return { dx: toward, dy: 0 };
  }

  if (axis === 'vertical') {
    return { dx: cx < imgW * 0.5 ? mag : -mag, dy: 0 };
  }
  if (axis === 'horizontal') {
    if (cy < imgH * 0.35) return { dx: 0, dy: mag };
    if (cy > imgH * 0.65) return { dx: 0, dy: -mag };
    return { dx: 0, dy: mag };
  }

  const rad = (stripRotationDeg * Math.PI) / 180;
  const px = Math.cos(rad + Math.PI / 2);
  const py = Math.sin(rad + Math.PI / 2);
  const dot = (cx - imgW / 2) * px + (cy - imgH / 2) * py;
  const sign = dot >= 0 ? -1 : 1;
  return { dx: sign * px * mag, dy: sign * py * mag };
}

function clampMaskCenter(cx: number, cy: number, imgW: number, imgH: number): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(imgW, cx)),
    y: Math.max(0, Math.min(imgH, cy)),
  };
}

/**
 * 在临时层绘制白蒙版后，以高斯模糊 + lighten 叠到主画布，使边缘呈弥散状、削弱细长锐芯。
 */
function compositeBlurredMaskLayer(
  targetCtx: CanvasRenderingContext2D,
  imgW: number,
  imgH: number,
  blurPx: number,
  drawWhiteOnBlack: (layerCtx: CanvasRenderingContext2D) => void,
): void {
  const layer = document.createElement('canvas');
  layer.width = imgW;
  layer.height = imgH;
  const lc = layer.getContext('2d');
  if (!lc) return;
  lc.fillStyle = '#000';
  lc.fillRect(0, 0, imgW, imgH);
  drawWhiteOnBlack(lc);

  targetCtx.save();
  targetCtx.globalCompositeOperation = 'lighten';
  targetCtx.filter = `blur(${Math.max(0.5, blurPx)}px)`;
  targetCtx.drawImage(layer, 0, 0);
  targetCtx.filter = 'none';
  targetCtx.restore();
}

/**
 * 灯带椭圆长轴旋转（度）：用户 rotation 优先；有 spatialNormal 时按顶/立面贴附，避免条带与墙悬空错位。
 */
function resolveCoveStripRotationDeg(
  fixture: Fixture,
  axis: StripAxis,
  rawRotation: number | undefined,
): number {
  if (Number.isFinite(rawRotation) && Math.abs(rawRotation as number) > 0.1) {
    return rawRotation as number;
  }
  const fallback = axis === 'vertical' ? 90 : 0;

  const n = fixture.spatialNormal;
  if (!n || typeof n.nx !== 'number' || typeof n.ny !== 'number' || typeof n.nz !== 'number') {
    return fallback;
  }

  const { nx, ny, nz } = n;
  const hLen = Math.hypot(nx, ny);

  // 近似水平面（天花/地面）：与 spatialBeamMask 一致，用 nx–nz 倾角
  if (Math.abs(ny) >= 0.58) {
    return (Math.atan2(-nx, nz) * 180) / Math.PI;
  }

  // 立面为主：长轴沿切向（图像平面内 ⊥ 法线投影）
  if (hLen >= 0.04) {
    return (Math.atan2(nx, -ny) * 180) / Math.PI;
  }

  return fallback;
}

/** 与 createCombinedMask 一致：哪些灯参与局部 inpaint 蒙版 */
export function fixtureContributesToCombinedMask(f: Fixture): boolean {
  if (!f.isModified) return false;
  const t = String(f.type || '').toLowerCase();
  const src = String((f as any).source || 'auto').toLowerCase();
  const localTypes = new Set([
    'spotlight',
    'downlight',
    'wall_lamp',
    'floor_lamp',
    'table_lamp',
    'cove_light',
    'cove_lighting',
  ]);
  if (!localTypes.has(t)) return false;
  if (src === 'manual') return true;
  return t === 'cove_light' || t === 'cove_lighting';
}

/**
 * 根据射灯参数生成黑白蒙版 (Mask)
 * @param fixture - 光源数据，包含 x, y, rotation, beamAngle
 * @param imgWidth - 原图宽度
 * @param imgHeight - 原图高度
 * @returns Base64 格式的蒙版图片
 */
export function createInpaintMask(
  fixture: Fixture,
  imgWidth: number,
  imgHeight: number
): string {
  const canvas = document.createElement('canvas');
  canvas.width = imgWidth;
  canvas.height = imgHeight;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('无法创建Canvas上下文');
  }

  // 1. 填充纯黑背景 (AI 将锁定此区域不进行修改)
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, imgWidth, imgHeight);

  // 2. 计算扇形参数
  // 将归一化坐标 (0-1000) 转换为像素坐标
  const x = (fixture.x / 1000) * imgWidth;
  const y = (fixture.y / 1000) * imgHeight;
  
  // 获取射灯参数
  const rotation = fixture.rotation ?? 90; // 默认向右
  const beamAngle = fixture.beamAngle ?? 30; // 默认30度
  
  // 🔥 使用与SpotlightInteractive相同的角度计算方式，确保蒙版与UI显示一致
  // 转换为弧度（直接使用，与SpotlightInteractive保持一致）
  const rotationRad = (rotation * Math.PI) / 180;
  const halfBeamRad = (beamAngle * Math.PI) / 180 / 2;
  const startAngle = rotationRad - halfBeamRad;
  const endAngle = rotationRad + halfBeamRad;
  
  console.log(`[蒙版生成] 光源角度: rotation=${rotation}°, beamAngle=${beamAngle}°, startAngle=${(startAngle * 180 / Math.PI).toFixed(1)}°, endAngle=${(endAngle * 180 / Math.PI).toFixed(1)}°`);
  
  // 计算光束半径（使用图片对角线的一部分，确保覆盖足够区域）
  const diagonal = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
  const radius = diagonal * 0.6; // 使用对角线的60%，确保光束足够长

  // 3. 绘制纯白扇形 (AI 仅在此区域内生成灯光)
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.arc(x, y, radius, startAngle, endAngle);
  ctx.closePath();
  ctx.fillStyle = 'white';
  ctx.fill();

  // 4. 关键步骤：添加羽化边缘，使光束边缘自然不生硬
  // 创建一个临时canvas用于模糊处理
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = imgWidth;
  tempCanvas.height = imgHeight;
  const tempCtx = tempCanvas.getContext('2d');
  
  if (tempCtx) {
    // 将当前绘制的内容复制到临时canvas
    tempCtx.drawImage(canvas, 0, 0);
    
    // 应用模糊滤镜（使用CSS filter，但需要转换为图像数据）
    // 注意：Canvas的filter属性支持有限，我们使用图像数据操作
    const imageData = tempCtx.getImageData(0, 0, imgWidth, imgHeight);
    const data = imageData.data;
    
    // 简单的边缘羽化：对边缘像素进行alpha混合
    const blurRadius = 15; // 羽化半径（像素）
    for (let i = 0; i < data.length; i += 4) {
      const pixelX = (i / 4) % imgWidth;
      const pixelY = Math.floor((i / 4) / imgWidth);
      
      // 检查是否在扇形边缘附近
      const distToCenter = Math.sqrt((pixelX - x) ** 2 + (pixelY - y) ** 2);
      const angleToPixel = Math.atan2(pixelY - y, pixelX - x);
      
      // 标准化角度到 [0, 2π]
      let normalizedAngle = angleToPixel;
      if (normalizedAngle < 0) normalizedAngle += 2 * Math.PI;
      
      // 检查角度是否在扇形范围内
      let startNorm = startAngle;
      let endNorm = endAngle;
      if (startNorm < 0) startNorm += 2 * Math.PI;
      if (endNorm < 0) endNorm += 2 * Math.PI;
      
      let isInSector = false;
      if (startNorm <= endNorm) {
        isInSector = normalizedAngle >= startNorm && normalizedAngle <= endNorm;
      } else {
        isInSector = normalizedAngle >= startNorm || normalizedAngle <= endNorm;
      }
      
      // 如果在扇形内，检查是否在边缘附近
      if (isInSector && distToCenter <= radius) {
        const distToEdge = Math.min(
          distToCenter, // 距离中心的距离
          radius - distToCenter, // 距离外边缘的距离
          Math.abs(normalizedAngle - startNorm) * radius, // 距离起始边的角度距离
          Math.abs(normalizedAngle - endNorm) * radius // 距离结束边的角度距离
        );
        
        // 在边缘附近应用渐变
        if (distToEdge < blurRadius) {
          const alpha = distToEdge / blurRadius;
          data[i + 3] = Math.round(255 * alpha); // 调整alpha通道
        }
      } else if (data[i + 3] > 0) {
        // 不在扇形内，但当前是白色，需要变黑
        data[i + 3] = 0;
      }
    }
    
    tempCtx.putImageData(imageData, 0, 0);
    
    // 将处理后的内容复制回原canvas
    ctx.clearRect(0, 0, imgWidth, imgHeight);
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, imgWidth, imgHeight);
    ctx.drawImage(tempCanvas, 0, 0);
  }

  // 5. 转换为Base64
  return canvas.toDataURL('image/png');
}

/**
 * 为多个定向下照光（射灯/筒灯）生成组合蒙版
 * @param fixtures - 光源数组（处理 spotlight/downlight 且已修改）
 * @param imgWidth - 原图宽度
 * @param imgHeight - 原图高度
 * @returns Base64 格式的组合蒙版图片
 */
export function createCombinedMask(
  fixtures: Fixture[],
  imgWidth: number,
  imgHeight: number
): string {
  const canvas = document.createElement('canvas');
  canvas.width = imgWidth;
  canvas.height = imgHeight;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('无法创建Canvas上下文');
  }

  // 1. 填充纯黑背景
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, imgWidth, imgHeight);

  // 2. 局部蒙版：手动新增灯 + 识别后用户调整过的槽灯/暗光（auto cove）
  const guideLights = fixtures.filter(fixtureContributesToCombinedMask);

  console.log(
    `[蒙版生成] 开始生成蒙版，图片尺寸: ${imgWidth}x${imgHeight}, 参与局部约束光源数量: ${guideLights.length}`,
  );
  
  const diagonal = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
  const shortEdge = Math.min(imgWidth, imgHeight);
  const drawSoftCircle = (targetCtx: CanvasRenderingContext2D, cx: number, cy: number, radius: number) => {
    const r = Math.max(10, radius);
    const g = targetCtx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.7, 'rgba(255,255,255,0.75)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    targetCtx.fillStyle = g;
    targetCtx.beginPath();
    targetCtx.arc(cx, cy, r, 0, Math.PI * 2);
    targetCtx.fill();
  };

  // 细长柔和灯带区域（用于背光/暗光带），避免生成圆形大光团
  type EllipseMaskMode = 'default' | 'indirectCore' | 'indirectCoreManual' | 'indirectWash';
  const drawSoftEllipse = (
    targetCtx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    rotationDeg: number = 0,
    mode: EllipseMaskMode = 'default',
  ) => {
    const safeRx = Math.max(10, rx);
    const safeRy = Math.max(4, ry);
    targetCtx.save();
    targetCtx.translate(cx, cy);
    targetCtx.rotate((rotationDeg * Math.PI) / 180);
    targetCtx.scale(safeRx, safeRy);
    const g = targetCtx.createRadialGradient(0, 0, 0, 0, 0, 1);
    if (mode === 'default') {
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.65, 'rgba(255,255,255,0.8)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
    } else if (mode === 'indirectCore') {
      // 中心不顶满 255，减轻「沿蒙版中心画一条白线」的倾向
      g.addColorStop(0, 'rgba(255,255,255,0.48)');
      g.addColorStop(0.3, 'rgba(255,255,255,0.94)');
      g.addColorStop(0.58, 'rgba(255,255,255,0.72)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
    } else if (mode === 'indirectCoreManual') {
      // 手动 cove：峰值 Alpha ≤0.8，进一步压低「灯管实体」诱导
      g.addColorStop(0, 'rgba(255,255,255,0.38)');
      g.addColorStop(0.28, `rgba(255,255,255,${MANUAL_COVE_CORE_ALPHA_MAX})`);
      g.addColorStop(0.55, 'rgba(255,255,255,0.58)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
    } else {
      g.addColorStop(0, 'rgba(255,255,255,0.26)');
      g.addColorStop(0.42, 'rgba(255,255,255,0.5)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
    }
    targetCtx.fillStyle = g;
    targetCtx.beginPath();
    targetCtx.arc(0, 0, 1, 0, Math.PI * 2);
    targetCtx.fill();
    targetCtx.restore();
  };

  const drawSector = (cx: number, cy: number, rotation: number, beam: number, radius: number) => {
    const rotationRad = (rotation * Math.PI) / 180;
    const halfBeamRad = (beam * Math.PI) / 180 / 2;
    const startAngle = rotationRad - halfBeamRad;
    const endAngle = rotationRad + halfBeamRad;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius, startAngle, endAngle);
    ctx.closePath();
    ctx.fillStyle = 'white';
    ctx.fill();
  };

  /**
   * 手动筒灯：圆形光斑蒙版 — 中心致密（高光核）、边缘多段近似平方反比/指数式衰减至高斯型透明，
   * 灰度即光强权重；光束外全透明，避免扇形硬边诱导大面积漫射。
   */
  const drawManualDownlightPhotometricDisc = (cx: number, cy: number, radiusPx: number) => {
    const R = Math.max(14, radiusPx);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.08, 'rgba(255,255,255,0.97)');
    g.addColorStop(0.16, 'rgba(255,255,255,0.86)');
    g.addColorStop(0.26, 'rgba(255,255,255,0.62)');
    g.addColorStop(0.38, 'rgba(255,255,255,0.36)');
    g.addColorStop(0.5, 'rgba(255,255,255,0.18)');
    g.addColorStop(0.62, 'rgba(255,255,255,0.08)');
    g.addColorStop(0.74, 'rgba(255,255,255,0.03)');
    g.addColorStop(0.86, 'rgba(255,255,255,0.01)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.fill();
  };

  for (const fixture of guideLights) {
    // 🔥 关键修复：检查坐标系统，确保是 0-1000 系统
    // 如果坐标 > 1000，说明是像素值，需要先归一化
    let normalizedX: number;
    let normalizedY: number;
    
    if (fixture.x > 1000 || fixture.y > 1000) {
      // 🔥 坐标是像素值，需要归一化（这种情况不应该发生，但为了安全起见）
      console.warn(`[蒙版生成] ⚠️  警告：光源 ${fixture.id} 的坐标是像素值而不是归一化值！`);
      console.warn(`[蒙版生成]   原始坐标: (${fixture.x}, ${fixture.y})`);
      console.warn(`[蒙版生成]   图片尺寸: ${imgWidth}x${imgHeight}`);
      // 假设坐标是基于图片尺寸的像素值，归一化到 0-1000
      normalizedX = (fixture.x / imgWidth) * 1000;
      normalizedY = (fixture.y / imgHeight) * 1000;
      console.warn(`[蒙版生成]   归一化后: (${normalizedX.toFixed(1)}, ${normalizedY.toFixed(1)})`);
    } else {
      // 🔥 坐标已经是 0-1000 系统，直接使用
      normalizedX = fixture.x;
      normalizedY = fixture.y;
    }
    
    // 🔥 确保坐标在有效范围内
    normalizedX = Math.max(0, Math.min(1000, normalizedX));
    normalizedY = Math.max(0, Math.min(1000, normalizedY));
    
    // 🔥 将归一化坐标 (0-1000) 转换为像素坐标（使用原图尺寸）
    const x = (normalizedX / 1000) * imgWidth;
    const y = (normalizedY / 1000) * imgHeight;
    const rawRotation = typeof fixture.rotation === 'number' ? fixture.rotation : undefined;
    const rotation = rawRotation ?? 90;
    const beamAngle = fixture.beamAngle ?? 30;
    const labelLower = String(fixture.label || '').toLowerCase();
    const isCeilingLight = fixture.type === 'downlight' && (labelLower.includes('吸顶') || labelLower.includes('ceiling'));
    const isDownlight = fixture.type === 'downlight' && !isCeilingLight;
    const isSpotlight = fixture.type === 'spotlight';
    const isWallLamp = fixture.type === 'wall_lamp';
    const isFloorOrTable = fixture.type === 'floor_lamp' || fixture.type === 'table_lamp';
    const isCoveLike = fixture.type === 'cove_light' || fixture.type === 'cove_lighting';
    const isBacklitLike =
      isCoveLike &&
      (
        labelLower.includes('背光') ||
        labelLower.includes('光带') ||
        labelLower.includes('backlit') ||
        labelLower.includes('light strip') ||
        labelLower.includes('灯带')
      );
    const emissionModeRaw = String((fixture as any).emissionMode || '').toLowerCase();
    const labelText = String(fixture.label || '').toLowerCase();
    const emissionMode =
      emissionModeRaw ||
      (labelText.includes('上照') || labelText.includes('up') ? (labelText.includes('下照') || labelText.includes('up+down') ? 'up_down' : 'up') : '') ||
      (labelText.includes('下照') || labelText.includes('down') ? 'down' : '');
    const emitUp = emissionMode === 'up' || emissionMode === 'up_down' || emissionMode === '';
    const emitDown = emissionMode === 'down' || emissionMode === 'up_down' || emissionMode === '';
    
    // 🔥 调试信息：输出关键参数
    console.log(`[蒙版生成] 光源 ${fixture.id}:`);
    console.log(`  - 原始坐标: (${fixture.x}, ${fixture.y})`);
    console.log(`  - 归一化坐标: (${normalizedX.toFixed(1)}, ${normalizedY.toFixed(1)}) / 1000`);
    console.log(`  - 图片尺寸: ${imgWidth}x${imgHeight}`);
    console.log(`  - 像素坐标: (${x.toFixed(1)}, ${y.toFixed(1)})`);
    console.log(`  - 坐标验证: x=${((x / imgWidth) * 100).toFixed(1)}% (${x < imgWidth * 0.1 ? '左边缘' : x > imgWidth * 0.9 ? '右边缘' : '中间'}), y=${((y / imgHeight) * 100).toFixed(1)}% (${y < imgHeight * 0.1 ? '上边缘' : y > imgHeight * 0.9 ? '下边缘' : '中间'})`);
    console.log(`  - 角度: rotation=${rotation}°, beamAngle=${beamAngle}°`);
    
    // 🔥 坐标对齐验证：检查坐标是否在合理范围内
    if (x < 0 || x > imgWidth || y < 0 || y > imgHeight) {
      console.warn(`[蒙版生成] ⚠️  警告：坐标超出图片范围！像素坐标 (${x.toFixed(1)}, ${y.toFixed(1)}) 超出图片尺寸 ${imgWidth}x${imgHeight}`);
    }
    if (x > imgWidth * 0.95 || y > imgHeight * 0.95) {
      console.warn(`[蒙版生成] ⚠️  警告：坐标接近边缘！像素坐标 (${x.toFixed(1)}, ${y.toFixed(1)}) 在图片 ${imgWidth}x${imgHeight} 的边缘区域`);
    }

    // 3. 按灯型绘制局部白区（白区=允许改动），保证“点哪亮哪”
    if (isSpotlight) {
      // 有法线时：椭圆物理蒙版（斜墙拉长）；否则扇形
      const isManualSp = fixture.source === 'manual';
      const beam = Math.max(10, Math.min(28, beamAngle || 16));
      const radius = isManualSp
        ? Math.min(diagonal * 0.3, shortEdge * 0.62)
        : diagonal * 0.52;
      if (!tryDrawSpatialBeamMask(ctx, fixture, imgWidth, imgHeight, diagonal, shortEdge)) {
        drawSector(x, y, rotation, beam, radius);
      }
    } else if (isDownlight) {
      const isManualDl = fixture.source === 'manual';
      const beam = Math.max(16, Math.min(36, beamAngle || 24));
      const radiusAuto = diagonal * 0.42;
      if (isManualDl) {
        const baseR = Math.min(diagonal * 0.26, shortEdge * 0.58);
        const radiusDisc = baseR * (0.82 + 0.18 * (beam / 36));
        drawManualDownlightPhotometricDisc(x, y, radiusDisc);
      } else if (!tryDrawSpatialBeamMask(ctx, fixture, imgWidth, imgHeight, diagonal, shortEdge)) {
        drawSector(x, y, rotation, beam, radiusAuto);
      }
    } else if (isCeilingLight) {
      // 吸顶灯：漫射泛光，局部大圆域
      drawSoftCircle(ctx, x, y, shortEdge * 0.26);
    } else if (isWallLamp) {
      // 壁灯：使用“柔和局部光斑”约束，避免模型把蒙版理解成卡通锥形光束
      if (emitUp) drawSoftCircle(ctx, x, y - shortEdge * 0.035, shortEdge * 0.075);
      if (emitDown) drawSoftCircle(ctx, x, y + shortEdge * 0.045, shortEdge * 0.085);
      drawSoftCircle(ctx, x, y, shortEdge * 0.045); // 本体附近微光
    } else if (isFloorOrTable) {
      // 落地灯/台灯：同样改为柔和局部区域，避免锥形“儿童画”观感
      drawSoftCircle(ctx, x, y, shortEdge * 0.08);
      if (emitDown) drawSoftCircle(ctx, x, y + shortEdge * 0.09, shortEdge * 0.14);
      if (emitUp) drawSoftCircle(ctx, x, y - shortEdge * 0.08, shortEdge * 0.12);
    } else if (isBacklitLike) {
      // 光带：线性柔光 + 外层洗光蒙版；走向由 stripAxis / stripContext 推断，可选手动 rotation 或 spatialNormal 贴墙倾角
      const stripCtxNorm = normalizeStripContext((fixture as any).stripContext);
      const axis = normalizeStripAxis((fixture as any).stripAxis) ?? defaultAxisForStripContext(stripCtxNorm);
      const { lenMul, thickMul } = maskScaleForStripContext(stripCtxNorm);
      const stripRotation = resolveCoveStripRotationDeg(fixture, axis, rawRotation);
      const stripLen = shortEdge * 0.34 * lenMul;
      const isManualCoveLight =
        String((fixture as any).source || '').toLowerCase() === 'manual' &&
        (fixture.type === 'cove_light' || fixture.type === 'cove_lighting');

      if (isManualCoveLight) {
        const off = manualCoveIlluminatedSurfaceOffsetPx(
          fixture,
          stripCtxNorm,
          axis,
          x,
          y,
          imgWidth,
          imgHeight,
          stripRotation,
        );
        const c = clampMaskCenter(x + off.dx, y + off.dy, imgWidth, imgHeight);
        const minThickPx = shortEdge * 0.1;
        const baseThin = shortEdge * 0.021 * thickMul;
        const dynamicFeatherMul = Math.max(1.2, minThickPx / Math.max(1e-6, baseThin));
        const stripThickness = Math.max(minThickPx, baseThin * dynamicFeatherMul);
        const blurPx = Math.max(8, shortEdge * 0.09);
        compositeBlurredMaskLayer(ctx, imgWidth, imgHeight, blurPx, (lc) => {
          drawSoftEllipse(lc, c.x, c.y, stripLen, stripThickness, stripRotation, 'indirectCoreManual');
          drawSoftEllipse(lc, c.x, c.y, stripLen * 1.12, stripThickness * 2.5, stripRotation, 'indirectWash');
        });
      } else {
        const stripFeather = fixture.source === 'manual' ? 1.5 : 1;
        const stripThickness = shortEdge * 0.021 * thickMul * stripFeather;
        drawSoftEllipse(ctx, x, y, stripLen, stripThickness, stripRotation, 'indirectCore');
        drawSoftEllipse(ctx, x, y, stripLen * 1.08, stripThickness * 2.35, stripRotation, 'indirectWash');
      }
    } else if (isCoveLike) {
      const isManualCoveLight =
        String((fixture as any).source || '').toLowerCase() === 'manual' &&
        (fixture.type === 'cove_light' || fixture.type === 'cove_lighting');
      if (isManualCoveLight) {
        const stripCtxNorm = normalizeStripContext((fixture as any).stripContext);
        const axis = normalizeStripAxis((fixture as any).stripAxis) ?? defaultAxisForStripContext(stripCtxNorm);
        const stripRotation = resolveCoveStripRotationDeg(fixture, axis, rawRotation);
        const off = manualCoveIlluminatedSurfaceOffsetPx(
          fixture,
          stripCtxNorm,
          axis,
          x,
          y,
          imgWidth,
          imgHeight,
          stripRotation,
        );
        const c = clampMaskCenter(x + off.dx, y + off.dy, imgWidth, imgHeight);
        const r = Math.max(shortEdge * 0.13 * 1.5, shortEdge * 0.1);
        const blurPx = Math.max(8, shortEdge * 0.09);
        compositeBlurredMaskLayer(ctx, imgWidth, imgHeight, blurPx, (lc) => {
          drawSoftCircle(lc, c.x, c.y, r);
        });
      } else {
        const manualCoveFeather = fixture.source === 'manual' ? 1.5 : 1;
        drawSoftCircle(ctx, x, y, shortEdge * 0.13 * manualCoveFeather);
      }
    } else {
      // 兜底：局部小范围
      drawSoftCircle(ctx, x, y, shortEdge * 0.12);
    }
    
  }

  console.log(`[蒙版生成] ✅ 蒙版生成完成`);

  return canvas.toDataURL('image/png');
}
